import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import NewsCard from "@/pages/news/components/NewsCard";
import { newsCategories, newsList } from "@/mocks/news";

const PAGE_SIZE = 4;
const ALL = "全部";

const sortOptions = [
  { value: "latest", label: "按时间" },
  { value: "hot", label: "按热度" },
];

export default function NewsList() {
  const [params, setParams] = useSearchParams();

  const keyword = params.get("q") ?? "";
  const category = params.get("category") ?? ALL;
  const sort = params.get("sort") ?? "latest";
  const page = Math.max(1, Number(params.get("page") ?? 1));

  usePageMeta({
    title: "资讯分类 - 张津瑜个人品牌官网",
    description: "了解张津瑜最新资讯和动态，关注个人品牌发展。",
    keywords: "张津瑜资讯,张津瑜动态,个人品牌资讯,活动公告",
  });

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    let list = [...newsList];
    if (category !== ALL) list = list.filter((n) => n.category === category);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(kw) ||
          n.summary.toLowerCase().includes(kw) ||
          n.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    list.sort((a, b) =>
      sort === "hot"
        ? b.views - a.views
        : a.date < b.date
          ? 1
          : -1,
    );
    return list;
  }, [category, keyword, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasFilter = category !== ALL || keyword.trim() !== "" || sort !== "latest";

  const resetFilters = () => setParams(new URLSearchParams(), { replace: true });

  return (
    <>
      <section className="bg-primary-800 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-400 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: "资讯中心" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            资讯中心
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            汇集张津瑜的最新动态、活动公告、行业观点与媒体访谈，用系统化的方式记录品牌的每一步成长。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-8 md:py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              {[ALL, ...newsCategories].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() =>
                    updateParams({ category: cat === ALL ? null : cat, page: null })
                  }
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    category === cat
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-sm text-foreground-500 sm:block">排序：</span>
              <div className="flex rounded-md border border-background-300 bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      updateParams({
                        sort: opt.value === "latest" ? null : opt.value,
                        page: null,
                      })
                    }
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                      sort === opt.value
                        ? "bg-accent-500 text-background-50"
                        : "text-foreground-700 hover:text-primary-600"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {keyword.trim() && (
            <p className="mt-5 text-sm text-foreground-600">
              搜索关键词：“<span className="font-medium text-primary-600">{keyword}</span>”，
              共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 条结果
            </p>
          )}

          {paged.length > 0 ? (
            <>
              <div className="mt-6 flex flex-col gap-5">
                {paged.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
              <Pagination
                current={safePage}
                total={totalPages}
                onChange={(p) => updateParams({ page: p === 1 ? null : String(p) })}
              />
            </>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                暂无相关资讯
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                没有找到符合当前筛选条件的资讯，试试更换关键词或重置筛选条件。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {hasFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    重置筛选
                  </button>
                )}
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}